/* eslint-disable no-param-reassign */
export const IntervalMin = 7920000;                // 2.2 hours
export const IntervalCheckpoint1 = 1900800000;      // 22 days
export const IntervalCheckpoint2  = 7776000000;     // 3 months
export const IntervalCheckpoint3 = 31104000000;     // Over a year.

const LogProgressMin  = Math.log(IntervalMin);
const LogProgressMax  = Math.log(IntervalCheckpoint2);
const LogScaledMax = Math.log(IntervalCheckpoint3);
const RangeMin = 0;
const RangeMax = 100;

// Activation for determining whether or not an item is ready to study.
export const ReadyToStudyCap = 0.85;

/**
 * Helps calculate progress on a logarithmic scale.
 *
 * @param {Number} reviewInterval
 * @param {Number} logMin
 * @param {Number} logMax
 * @returns {Number}
 */
function reviewIntervalToProgress(reviewInterval, logMin = LogProgressMin, logMax = LogProgressMax) {
  // If review interval is negative or 0, the log method will return NaN or -Infinity.
  reviewInterval = reviewInterval && reviewInterval >= IntervalMin ? reviewInterval : IntervalMin;

  // Linear scale from 0 to 1.
  const progressScale = (Math.log(reviewInterval) - logMin) / (logMax - logMin);

  // Scale it to the specified range.
  let progress = progressScale * (RangeMax - RangeMin) + RangeMin;

  // Bound or round.
  if (progress >= RangeMax) {
    progress = RangeMax;
  } else if (progress >= RangeMax - 1) {
    progress = RangeMax - 1;
  } else if (progress <= 0) {
    progress = 0;
  } else if (progress <= 1) {
    progress = 1;
  }

  return Math.round(progress);
}

export class Memory {
  constructor(attrs = {}) {
    // Don't want to override our getter method.
    attrs = _.omit(attrs, 'progress');

    _.defaults(this, attrs, {
      contentDomain: null,
      contentId: null,
      skipped: false,
      reviewInterval: null,
      currentRetention: null,
      easinessModifier: null,
      lastStudyTime: null,
      seeNextAt: null,
      shortTermMemory: false,
      difficultyBucket: null,

      // Not returned by the API, used for help with memory changes.
      previous: null,
    });

    this._setDates();
  }

  get progress() {
    return reviewIntervalToProgress(this.reviewInterval);
  }

  /**
   * Returns the progress on a scale of 1 - 120, where 100% is the maximum of
   * the normal progress.
   */
  getScaledProgress() {
    // Because being exactly on interval checkpoint 2
    return this.progress + reviewIntervalToProgress(this.reviewInterval, LogProgressMax, LogScaledMax) / 5;
  }

  /**
   * If a previous memory is specified, use that to compare a difference in
   * progress.
   */
  getProgressDifference() {
    return this.previous ? this.progress - this.previous.progress : 0;
  }

  /**
   * Returns whether or not the memory is ready to study.
   *
   * @returns {Boolean}
   */
  isReady() {
    return !this.shortTermMemory && this.currentRetention < ReadyToStudyCap;
  }

  /**
   * @returns {Boolean}
   */
  isComplete() {
    return this.progress === 100;
  }

  /**
   * Converts properties that are strings to Date objects.
   */
  _setDates() {
    if (this.seeNextAt && !(this.seeNextAt instanceof Date)) {
      this.seeNextAt = new Date(this.seeNextAt);
    }

    if (this.lastStudyTime && !(this.lastStudyTime instanceof Date)) {
      this.lastStudyTime = new Date(this.lastStudyTime);
    }
  }

  /**
   * Changes the review interval to be the maximum interval.
   */
  setToComplete() {
    this.reviewInterval = IntervalCheckpoint3;
  }

  /**
   * Used to update memory for any memory changes that come back.
   * Will make a copy of the previous memory.
   */
  update(memories = {}) {
    this.previous = new Memory(this);

    // Swap out changes.
    Object.keys(memories).forEach(key => {
      // Prevent overriding the getter methods.
      if (this.hasOwnProperty(key)) {
        this[key] = memories[key];
      }
    });

    this._setDates();
  }
}

export default Memory;
